/*
 * UV announcement bar
 *
 * G1.1 — scaffold: state rendering, banding/rounding helpers, dismiss, and
 * --announcement-height syncing. The live OpenUV fetch lands in G1.2; until
 * then the bar renders from ?uv= (preview/QA) or stays hidden.
 *
 * Banding uses the RAW uv value; the DISPLAYED number is rounded half-up.
 * Moderate is absorbed into High, Extreme into Very high (approved 2026-08-04).
 */
class UVAnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.config = {
      apiKey: this.dataset.apiKey || '',
      cacheMinutes: parseInt(this.dataset.cacheMinutes, 10) || 60,
      timeoutMs: parseInt(this.dataset.timeoutMs, 10) || 8000,
      // Upper bound of each band (exclusive). WHO scale, client-confirmed
      // 2026-08-06: 0-2 low / 3-5 moderate / 6-7 high / 8-10 very high / 11+ extreme.
      lowMax: parseFloat(this.dataset.lowMax) || 3,
      moderateMax: parseFloat(this.dataset.moderateMax) || 6,
      highMax: parseFloat(this.dataset.highMax) || 8,
      veryhighMax: parseFloat(this.dataset.veryhighMax) || 11,
      sectionId: this.dataset.sectionId || '',
      designMode: this.dataset.designMode === 'true',
      fallbackMode: this.dataset.fallbackMode || 'hide',
    };

    this.fallbackMessage = this.dataset.fallbackMessage || '';

    this.states = {
      low: {
        label: this.dataset.lowLabel,
        message: this.dataset.lowMessage,
        reminder: this.dataset.lowReminder,
      },
      moderate: {
        label: this.dataset.moderateLabel,
        message: this.dataset.moderateMessage,
        reminder: this.dataset.moderateReminder,
      },
      high: {
        label: this.dataset.highLabel,
        message: this.dataset.highMessage,
        reminder: this.dataset.highReminder,
      },
      veryhigh: {
        label: this.dataset.veryhighLabel,
        message: this.dataset.veryhighMessage,
        reminder: this.dataset.veryhighReminder,
      },
      extreme: {
        label: this.dataset.extremeLabel,
        message: this.dataset.extremeMessage,
        reminder: this.dataset.extremeReminder,
      },
    };

    this.storageKey = 'glowbar:uv-bar:dismissed';
    this.cacheKey = 'glowbar:uv-bar:reading';

    this.els = {
      value: this.querySelector('[data-uv-value]'),
      category: this.querySelector('[data-uv-category]'),
      message: this.querySelector('[data-uv-message]'),
      reminderSuffix: this.querySelector('[data-uv-reminder-suffix]'),
      close: this.querySelector('[data-uv-close]'),
    };

    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    // The theme's page-transition system detaches and re-attaches sections, so
    // connectedCallback can fire more than once for the same element. Without
    // this guard the OpenUV lookup runs twice per pageview — observed ~5s
    // apart — which doubles consumption of a 50/day quota.
    if (this.initialised) return;
    this.initialised = true;

    if (this.els.close) {
      this.els.close.addEventListener('click', () => this.dismiss());
    }

    // Dismissal is session-only (approved 2026-08-04). In the theme editor we
    // always show the bar so the merchant can style it.
    if (!this.config.designMode && this.isDismissed()) {
      this.syncAnnouncementHeight(0);
      return;
    }

    this.init();

    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  async init() {
    // ?uv= override stays available for QA of each state without burning quota.
    const override = this.readUVOverride();
    if (override !== null) {
      this.render(override);
      return;
    }

    // ?uvfail=<reason> forces a fallback path for QA without having to wait for
    // a real denial/timeout/quota exhaustion.
    const forcedFailure = this.readParam('uvfail');
    if (forcedFailure) {
      this.renderFallback(forcedFailure);
      return;
    }

    if (!this.config.apiKey) {
      this.renderFallback('no-api-key');
      return;
    }

    // A cached reading avoids a geolocation prompt and an API call on every
    // pageview. The free tier is 50 requests/day, so this is load-bearing.
    const cached = this.readCache();
    if (cached !== null) {
      this.render(cached);
      return;
    }

    try {
      // Module-scoped in-flight guard: if a second instance (or a re-attached
      // element) starts while the first lookup is still pending, reuse the same
      // promise instead of issuing another billable request.
      if (!UVAnnouncementBar.inFlight) {
        UVAnnouncementBar.inFlight = (async () => {
          const coords = await this.getPosition();
          return this.fetchUV(coords.latitude, coords.longitude);
        })();
      }
      const uv = await UVAnnouncementBar.inFlight;
      if (uv === null) {
        // Reached the API but got a non-200 or an unparseable body
        // (403 bad key / 429 quota exhausted / 500 bad params / offline).
        UVAnnouncementBar.inFlight = null;
        this.renderFallback('api-error');
        return;
      }
      this.writeCache(uv);
      this.render(uv);
    } catch (e) {
      // Clear the shared promise so a later attempt is not blocked by a
      // transient failure.
      UVAnnouncementBar.inFlight = null;

      const code = e && e.code;
      let reason = 'geolocation-error';
      if (e && e.message === 'geolocation-unsupported') reason = 'geolocation-unsupported';
      else if (e && e.message === 'geolocation-timeout') reason = 'timeout';
      else if (code === 1) reason = 'permission-denied';
      else if (code === 2) reason = 'position-unavailable';
      else if (code === 3) reason = 'timeout';

      this.renderFallback(reason);
    }
  }

  /*
   * Fallback when no UV reading can be obtained (permission denied, position
   * unavailable, timeout, API error, quota exhausted, missing key).
   *
   * Default behaviour is to HIDE the bar: a location-aware UV bar with no
   * location has nothing truthful to say, and showing a bar that implies a
   * reading it does not have would be worse than showing nothing. The merchant
   * can opt into a generic SPF message instead via the section setting.
   */
  renderFallback(reason) {
    this.dataset.uvFallback = reason;

    // If the visitor later grants location (site settings, or a late prompt),
    // recover automatically instead of leaving the fallback stuck all session.
    if (reason === 'permission-denied' || reason === 'timeout' || reason === 'geolocation-error') {
      this.watchForLatePermission();
    }

    if (this.config.fallbackMode !== 'message' || !this.fallbackMessage) {
      this.hidden = true;
      this.syncAnnouncementHeight(0);
      return;
    }

    // Generic mode: no number, no category, and critically no "CURRENT UV:"
    // label — a label with nothing after it reads as a broken UV bar. Keep the
    // sun icon (brand furniture) and drop the rest of the reading group.
    const reading = this.querySelector('.uv-bar__group--reading');
    const message = this.querySelector('.uv-bar__group--message');
    const dividers = this.querySelectorAll('.uv-bar__divider');

    if (reading) {
      ['.uv-bar__label', '[data-uv-value]', '[data-uv-category]'].forEach((sel) => {
        const el = reading.querySelector(sel);
        if (el) el.hidden = true;
      });
    }
    dividers.forEach((d) => { d.hidden = true; });
    if (message) {
      message.hidden = false;
      const span = message.querySelector('[data-uv-message]');
      if (span) {
        // Keep the SPF link reachable: the reminder group (its usual home) is
        // hidden in fallback mode, so wrap the message itself when a URL is set.
        const url = this.dataset.spfUrl;
        if (url) {
          span.textContent = '';
          const a = document.createElement('a');
          a.className = 'uv-bar__spf-link';
          a.href = url;
          a.textContent = this.fallbackMessage;
          span.appendChild(a);
        } else {
          span.textContent = this.fallbackMessage;
        }
      }
    }

    // The reminder's suffix is UV-level-specific ("is non-negotiable."), so it
    // cannot be shown without a reading. Hide the whole reminder group — the
    // fallback message already carries the SPF prompt.
    const reminder = this.querySelector('.uv-bar__group--reminder');
    if (reminder) reminder.hidden = true;

    // Fallback uses the LOW palette — the least alarming of the three, since we
    // are not claiming a specific UV level.
    this.style.setProperty('--uv-state-background', 'var(--uv-low-background)');
    this.style.setProperty('--uv-state-accent', 'var(--uv-low-accent)');

    this.hidden = false;
    this.syncAnnouncementHeight();
  }

  /*
   * Geolocation with an explicit timeout. The browser's own timeout option can
   * hang indefinitely on some platforms when the prompt is ignored, so the
   * promise is raced against our own timer.
   */
  getPosition() {
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('geolocation-unsupported'));
        return;
      }

      let settled = false;

      /*
       * DO NOT race the permission prompt against a timer.
       *
       * The prompt is modal and people read it — taking longer than our
       * timeout to click "Allow" is normal behaviour, not a failure. A timer
       * here fired while the visitor was still deciding, we rendered the
       * fallback, and the late "Allow" was discarded: the visitor allowed
       * location and still saw the generic bar for the rest of the session.
       * (Reported by the client 2026-08-07; reproduced at a 12s click.)
       *
       * The browser applies its own `timeout` to ACQUIRING a fix once
       * permission is granted, which is the part that can legitimately hang.
       * Waiting on the human is not our timer's job.
       */
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (settled) return;
          settled = true;
          resolve(pos.coords);
        },
        (err) => {
          if (settled) return;
          settled = true;
          reject(err);
        },
        {
          enableHighAccuracy: false,
          // applies AFTER permission is granted, to the fix itself
          timeout: this.config.timeoutMs,
          maximumAge: this.config.cacheMinutes * 60 * 1000,
        }
      );
    });
  }

  /*
   * Watch for a permission grant that arrives after we have already fallen
   * back — e.g. the visitor allows location from the browser's site-settings
   * UI, or a queued prompt resolves late. Without this the fallback would
   * stick for the whole session even though we now could get a real reading.
   */
  watchForLatePermission() {
    if (!navigator.permissions || !navigator.permissions.query) return;
    if (this.permissionWatchBound) return;
    this.permissionWatchBound = true;

    navigator.permissions.query({ name: 'geolocation' }).then((status) => {
      status.addEventListener('change', () => {
        if (status.state !== 'granted') return;
        // a real reading is possible again — clear the fallback and retry
        UVAnnouncementBar.inFlight = null;
        delete this.dataset.uvFallback;
        this.restoreFromFallback();
        this.init();
      });
    }).catch(() => { /* Permissions API unavailable — nothing to watch */ });
  }

  /*
   * Undo renderFallback()'s DOM changes so a subsequent real reading renders
   * a complete bar (label, value, category, dividers and reminder restored).
   */
  restoreFromFallback() {
    const reading = this.querySelector('.uv-bar__group--reading');
    if (reading) {
      ['.uv-bar__label', '[data-uv-value]', '[data-uv-category]'].forEach((sel) => {
        const el = reading.querySelector(sel);
        if (el) el.hidden = false;
      });
    }
    this.querySelectorAll('.uv-bar__divider').forEach((d) => { d.hidden = false; });
    const reminder = this.querySelector('.uv-bar__group--reminder');
    if (reminder) reminder.hidden = false;

    // the fallback replaced the message span's contents with a link
    const span = this.querySelector('[data-uv-message]');
    if (span) span.textContent = '';
  }

  /*
   * OpenUV lookup. Coordinates are rounded to 2dp (~1km) — more precision has
   * no effect on the UV reading and avoids sending exact user coordinates.
   */
  async fetchUV(lat, lng) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => controller.abort(), this.config.timeoutMs)
      : null;

    try {
      const url = 'https://api.openuv.io/api/v1/uv?lat=' +
        encodeURIComponent(lat.toFixed(2)) + '&lng=' + encodeURIComponent(lng.toFixed(2));

      const res = await fetch(url, {
        method: 'GET',
        headers: { 'x-access-token': this.config.apiKey },
        signal: controller ? controller.signal : undefined,
      });

      if (timer) clearTimeout(timer);

      // 403 = bad/expired key, 429 = daily quota exhausted, 500 = bad params.
      if (!res.ok) return null;

      const data = await res.json();
      const uv = data && data.result ? parseFloat(data.result.uv) : NaN;
      return Number.isFinite(uv) ? uv : null;
    } catch (e) {
      if (timer) clearTimeout(timer);
      return null;
    }
  }

  readCache() {
    try {
      const raw = window.sessionStorage.getItem(this.cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const ageMs = Date.now() - (parsed.t || 0);
      if (ageMs > this.config.cacheMinutes * 60 * 1000) return null;
      const uv = parseFloat(parsed.uv);
      return Number.isFinite(uv) ? uv : null;
    } catch (e) {
      return null;
    }
  }

  writeCache(uv) {
    try {
      window.sessionStorage.setItem(this.cacheKey, JSON.stringify({ uv: uv, t: Date.now() }));
    } catch (e) {
      /* storage unavailable — the reading is simply refetched next pageview */
    }
  }

  readUVOverride() {
    const raw = parseFloat(this.readParam('uv'));
    return Number.isFinite(raw) ? raw : null;
  }

  readParam(name) {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.has(name) ? params.get(name) : null;
    } catch (e) {
      return null;
    }
  }

  /* Band on the RAW value. */
  /*
   * Band on the RAW value across five WHO bands. Thresholds are exclusive
   * upper bounds, so the default 3/6/8/11 gives:
   *   0-2.9 low | 3-5.9 moderate | 6-7.9 high | 8-10.9 very high | 11+ extreme
   */
  bandFor(uv) {
    if (uv < this.config.lowMax) return 'low';
    if (uv < this.config.moderateMax) return 'moderate';
    if (uv < this.config.highMax) return 'high';
    if (uv < this.config.veryhighMax) return 'veryhigh';
    return 'extreme';
  }

  /* Display value: round half-up to a whole number. */
  displayValue(uv) {
    return String(Math.round(uv));
  }

  render(uv) {
    const band = this.bandFor(uv);
    const state = this.states[band];
    if (!state) return;

    if (this.els.value) this.els.value.textContent = this.displayValue(uv);
    if (this.els.category) this.els.category.textContent = state.label || '';
    if (this.els.message) this.els.message.textContent = state.message || '';
    // HTML attribute parsing strips a leading space, so " is non-negotiable."
    // arrives as "is non-negotiable." and renders as "SPFis non-negotiable.".
    // Re-insert the separator unless the suffix opens with punctuation that
    // should hug the link (e.g. ", always.").
    if (this.els.reminderSuffix) {
      const suffix = state.reminder || '';
      const hugsLink = /^[,.;:!?)]/.test(suffix);
      this.els.reminderSuffix.textContent =
        suffix && !hugsLink ? ' ' + suffix.replace(/^\s+/, '') : suffix;
    }

    this.dataset.uvBand = band;
    this.style.setProperty('--uv-state-background', `var(--uv-${band}-background)`);
    this.style.setProperty('--uv-state-accent', `var(--uv-${band}-accent)`);

    this.hidden = false;
    this.syncAnnouncementHeight();
  }

  /*
   * Keep --announcement-height in sync.
   *
   * The theme computes calc(100vh - var(--announcement-height)) in the
   * slideshow, search overlay, predictive search, reveal banner and
   * transparent header. If this bar adds height without updating the variable,
   * those surfaces are sized wrong. Verified in G1.7.
   */
  syncAnnouncementHeight(explicitHeight) {
    const height = typeof explicitHeight === 'number'
      ? explicitHeight
      : this.offsetHeight;

    document.documentElement.style.setProperty('--uv-bar-height', `${height}px`);

    const existing = this.readThemeAnnouncementHeight();
    document.documentElement.style.setProperty(
      '--announcement-height',
      `${existing + height}px`
    );
  }

  /*
   * The theme's own announcement bar may already contribute height. Measure it
   * directly so we add to it rather than clobbering it.
   */
  readThemeAnnouncementHeight() {
    const themeBar = document.querySelector('.announcement-bar');
    if (!themeBar) return 0;
    if (themeBar.closest('uv-announcement-bar')) return 0;
    return themeBar.offsetHeight || 0;
  }

  onResize() {
    if (this.hidden) return;
    this.syncAnnouncementHeight();
  }

  isDismissed() {
    try {
      return window.sessionStorage.getItem(this.storageKey) === '1';
    } catch (e) {
      return false;
    }
  }

  dismiss() {
    try {
      window.sessionStorage.setItem(this.storageKey, '1');
    } catch (e) {
      /* storage unavailable (private mode) — dismiss for this page view only */
    }

    this.hidden = true;
    this.syncAnnouncementHeight(0);
  }
}

if (!customElements.get('uv-announcement-bar')) {
  customElements.define('uv-announcement-bar', UVAnnouncementBar);
}
